/**
 * An invoice's payments (Phase 21 / Task 21.7d, on 21.6a's API): the ledger, recording money
 * received, and reversing one payment.
 *
 *   GET  /invoices/:id/payments   the ledger, oldest first (a reversal is its own row)
 *   POST /invoices/:id/payments   {amount dollars, paymentDate, method, reference?, notes?}
 *   POST /payments/:id/reverse    {reason}
 *
 * Payments go against a SENT invoice only, part or all of its balance, never more; the invoice
 * reads PAID when the balance reaches 0. A mistake is a reversal (with a reason), which reopens a
 * PAID invoice. Nothing is edited or deleted. The API re-checks every rule.
 */
import * as React from 'react';
import { toast } from 'sonner';
import { Button } from 'src/components/ui/button';
import { FormRow, Pill, fieldStyle, subText } from '../../ui/bits';
import {
  PAYMENT_METHODS,
  useBillingToday,
  useInvoiceMutation,
  useInvoicePayments,
  type Invoice,
  type Payment,
  type PaymentMethod,
  type RecordPaymentBody,
} from './billing-hooks';
import { billingErrorText } from './billing-format';
import { money } from './invoice-format';
import { PAYMENT_METHOD_LABELS, paymentErrors } from './payment-format';

const cell: React.CSSProperties = { padding: '4px 6px' };

function RecordPayment({ inv, today }: { inv: Invoice; today: string }): React.JSX.Element {
  const [amount, setAmount] = React.useState('');
  const [paymentDate, setPaymentDate] = React.useState(today);
  const [method, setMethod] = React.useState<PaymentMethod>('ACH');
  const [reference, setReference] = React.useState('');
  const [notes, setNotes] = React.useState('');
  const record = useInvoiceMutation<RecordPaymentBody, { invoice: Invoice }>(
    `/invoices/${inv.id}/payments`,
    'POST',
    {
      onSuccess: () => {
        setAmount('');
        setReference('');
        setNotes('');
      },
      onError: (err) => toast.error(billingErrorText(err, 'Could not record the payment.')),
    },
  );
  const errors = paymentErrors(
    { amount, paymentDate },
    { balanceCents: inv.balanceCents, issueDate: inv.issueDate, today },
  );
  const touched = amount.trim() !== '';
  const ready = Object.keys(errors).length === 0;
  return (
    <form
      onSubmit={(e): void => {
        e.preventDefault();
        if (!ready || record.isPending) return;
        record.mutate(
          {
            amount: amount.trim(),
            paymentDate,
            method,
            reference: reference.trim() === '' ? null : reference.trim(),
            notes: notes.trim() === '' ? null : notes.trim(),
          },
          {
            onSuccess: (res) =>
              toast.success(
                res.invoice.status === 'PAID'
                  ? 'Payment recorded. The invoice is paid in full.'
                  : `Payment recorded. ${money(res.invoice.balanceCents)} still due.`,
              ),
          },
        );
      }}
      style={{
        display: 'flex',
        gap: '8px',
        alignItems: 'end',
        flexWrap: 'wrap',
        marginTop: '12px',
      }}
    >
      <FormRow label="Amount" htmlFor="pay-amount" required>
        <input
          id="pay-amount"
          value={amount}
          placeholder={money(inv.balanceCents).replace('$', '')}
          onChange={(e): void => setAmount(e.target.value)}
          style={{ ...fieldStyle, width: '120px' }}
          aria-invalid={touched && errors.amount !== undefined}
        />
      </FormRow>
      <FormRow label="Paid on" htmlFor="pay-date" required>
        <input
          id="pay-date"
          type="date"
          value={paymentDate}
          max={today}
          onChange={(e): void => setPaymentDate(e.target.value)}
          style={fieldStyle}
        />
      </FormRow>
      <FormRow label="Method" htmlFor="pay-method">
        <select
          id="pay-method"
          value={method}
          onChange={(e): void => setMethod(e.target.value as PaymentMethod)}
          style={fieldStyle}
        >
          {PAYMENT_METHODS.map((m) => (
            <option key={m} value={m}>
              {PAYMENT_METHOD_LABELS[m]}
            </option>
          ))}
        </select>
      </FormRow>
      <FormRow label="Reference" htmlFor="pay-ref">
        <input
          className="keep-case"
          id="pay-ref"
          value={reference}
          maxLength={100}
          placeholder="Check no. or transfer id"
          onChange={(e): void => setReference(e.target.value)}
          style={{ ...fieldStyle, width: '160px' }}
        />
      </FormRow>
      <FormRow label="Notes" htmlFor="pay-notes">
        <input
          className="keep-case"
          id="pay-notes"
          value={notes}
          maxLength={4000}
          onChange={(e): void => setNotes(e.target.value)}
          style={fieldStyle}
        />
      </FormRow>
      <Button type="submit" disabled={!ready || record.isPending}>
        Record payment
      </Button>
      {touched && (errors.amount !== undefined || errors.paymentDate !== undefined) && (
        <p
          role="alert"
          style={{ ...subText, color: 'var(--color-danger)', width: '100%', margin: 0 }}
        >
          {[errors.amount, errors.paymentDate].filter((x) => x !== undefined).join('. ')}
        </p>
      )}
    </form>
  );
}

function ReversePayment({ p }: { p: Payment }): React.JSX.Element {
  const [open, setOpen] = React.useState(false);
  const [reason, setReason] = React.useState('');
  const reverse = useInvoiceMutation<{ reason: string }, unknown>(
    `/payments/${p.id}/reverse`,
    'POST',
    {
      onSuccess: () => {
        setOpen(false);
        setReason('');
        toast.success('Payment reversed. The balance is due again.');
      },
      onError: (err) => toast.error(billingErrorText(err, 'Could not reverse the payment.')),
    },
  );
  if (!open) {
    return (
      <Button type="button" variant="ghost" onClick={(): void => setOpen(true)}>
        Reverse
      </Button>
    );
  }
  return (
    <span style={{ display: 'inline-flex', gap: '6px', alignItems: 'center' }}>
      <input
        className="keep-case"
        aria-label="Why it is reversed (audited)"
        value={reason}
        maxLength={1000}
        placeholder="Why (e.g. cheque bounced)"
        onChange={(e): void => setReason(e.target.value)}
        style={{ ...fieldStyle, width: '200px' }}
      />
      <Button
        type="button"
        variant="destructive"
        disabled={reason.trim().length < 3 || reverse.isPending}
        onClick={(): void => reverse.mutate({ reason: reason.trim() })}
      >
        Confirm reversal
      </Button>
      <Button type="button" variant="ghost" onClick={(): void => setOpen(false)}>
        Cancel
      </Button>
    </span>
  );
}

export function InvoicePayments({ inv }: { inv: Invoice }): React.JSX.Element {
  const { data, isLoading, isError } = useInvoicePayments(inv.id);
  const today = useBillingToday();
  const rows = data?.payments ?? [];
  return (
    <section data-testid="invoice-payments" style={{ marginTop: '14px' }}>
      <h3 style={{ fontSize: '15px', fontWeight: 600, margin: '0 0 6px' }}>Payments</h3>
      {inv.paidOn !== null && (
        <p style={{ ...subText, marginTop: 0 }} data-testid="paid-on">
          Paid in full on {inv.paidOn}.
        </p>
      )}
      {isLoading ? (
        <p style={subText}>Loading…</p>
      ) : isError ? (
        <p role="alert" style={{ color: 'var(--color-danger)' }}>
          Could not load the payments.
        </p>
      ) : rows.length === 0 ? (
        <p style={subText}>No payments yet.</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr style={{ textAlign: 'left', ...subText }}>
              <th style={cell}>Paid on</th>
              <th style={cell}>Method</th>
              <th style={cell}>Reference</th>
              <th style={{ ...cell, textAlign: 'right' }}>Amount</th>
              <th style={cell} />
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr
                key={p.id}
                data-testid={`payment-${p.id}`}
                style={{ borderTop: '1px solid var(--color-border-primary)' }}
              >
                <td style={cell}>{p.paymentDate}</td>
                <td style={cell}>
                  {p.kind === 'REVERSAL' ? (
                    <Pill tone="warning" label="Reversal" />
                  ) : p.method === null ? (
                    '—'
                  ) : (
                    PAYMENT_METHOD_LABELS[p.method]
                  )}
                </td>
                <td style={cell}>
                  {p.kind === 'REVERSAL' ? (p.reason ?? '') : (p.reference ?? '—')}
                  {p.kind === 'PAYMENT' && p.reversedByPaymentId !== null && (
                    <span style={subText}> · reversed</span>
                  )}
                </td>
                <td style={{ ...cell, textAlign: 'right' }}>
                  {p.kind === 'REVERSAL' ? `−${money(p.amountCents)}` : money(p.amountCents)}
                </td>
                <td style={{ ...cell, textAlign: 'right' }}>
                  {p.kind === 'PAYMENT' &&
                    p.reversedByPaymentId === null &&
                    inv.status !== 'VOID' && <ReversePayment p={p} />}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {inv.status === 'SENT' && today !== null && <RecordPayment inv={inv} today={today} />}
    </section>
  );
}
